import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, Menu, X } from "lucide-react";
import { CONTACT, IMAGES } from "../data/content";

const NAV_LINKS = [
  { label: "The Estate", href: "#estate" },
  { label: "Stays", href: "#stays" },
  { label: "Experience", href: "#experience" },
  { label: "Reviews", href: "#reviews" },
];

export const Navbar = ({ onEnquire }) => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="main-navbar"
      className={`fixed top-0 left-0 w-full z-40 transition-all duration-500 ${
        scrolled
          ? "bg-[#243E2C]/95 backdrop-blur-md border-b border-white/10 py-2"
          : "bg-gradient-to-b from-black/50 to-transparent py-4"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between">
        <a href="#top" data-testid="navbar-logo" className="flex items-center">
          <img
            src={IMAGES.logo}
            alt="Nine Furlongs Binsar"
            className={`w-auto transition-all duration-500 ${
              scrolled ? "h-12 md:h-14" : "h-14 md:h-16"
            }`}
          />
        </a>

        <nav className="hidden md:flex items-center gap-9">
          {NAV_LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              className="text-sm tracking-wide text-[#EFECE4] transition-opacity hover:opacity-70"
            >
              {l.label}
            </a>
          ))}
          <a
            href={CONTACT.phoneHref}
            data-testid="navbar-call-link"
            className="flex items-center gap-2 text-sm tracking-wide text-[#EFECE4] hover:text-[#C4A575] transition-colors"
          >
            <Phone size={15} /> {CONTACT.phone}
          </a>
          <button
            onClick={onEnquire}
            data-testid="navbar-enquire-button"
            className="bg-[#C4A575] text-primary px-6 py-3 text-xs uppercase tracking-[0.18em] hover:bg-[#b3935f] transition-colors"
          >
            Enquire
          </button>
        </nav>

        <button
          className="md:hidden"
          onClick={() => setOpen(true)}
          data-testid="navbar-mobile-toggle"
          aria-label="Open menu"
        >
          <Menu className="text-[#EFECE4]" />
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-primary z-50 flex flex-col p-8 md:hidden"
            data-testid="mobile-menu"
          >
            <div className="flex justify-between items-center mb-16">
              <img src={IMAGES.logo} alt="Nine Furlongs" className="h-14 w-auto" />
              <button onClick={() => setOpen(false)} aria-label="Close menu" data-testid="mobile-menu-close">
                <X className="text-[#F6F5F0]" />
              </button>
            </div>
            <nav className="flex flex-col gap-7">
              {NAV_LINKS.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  className="font-serif text-3xl text-[#F6F5F0]"
                >
                  {l.label}
                </a>
              ))}
            </nav>
            <div className="mt-auto flex flex-col gap-4">
              <a
                href={CONTACT.phoneHref}
                className="flex items-center gap-2 text-[#C4A575] text-lg"
              >
                <Phone size={18} /> {CONTACT.phone}
              </a>
              <button
                onClick={() => {
                  setOpen(false);
                  onEnquire();
                }}
                className="bg-[#C4A575] text-primary px-6 py-4 text-sm uppercase tracking-[0.18em]"
              >
                Enquire Now
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
